import React, { useState } from "react";
import Box from "@mui/material/Box";

interface MagnifierImageProps {
    src: string;
    zoom?: number;
    width?: number;
    height?: number;
    onClick?: () => void;
    disabled?: boolean;
}

/**
 * 
 * @param {string} src image path
 * @param {number} zoom
 * @param {number} width image width
 * @param {number} height image height
 * @param {void} onClick function onClick of the image
 * @param {boolean} disabled disable the onClick function and zoom
 * @returns 
 */
const MagnifierImage: React.FC<MagnifierImageProps> = ({
    src,
    zoom = 2,
    width = 200,
    height = 200,
    onClick = () => {},
    disabled = false,
}) => {
    const [magnifierPosition, setMagnifierPosition] = useState<{
        x: number;
        y: number;
    }>({ x: 0, y: 0 });
    const [showMagnifier, setShowMagnifier] = useState<boolean>(false);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const { offsetX, offsetY } = e.nativeEvent;

        // calcola i limiti massimi per evitare che l'immagine ingrandita si sposti oltre i bordi
        const maxX = width - 1; // Impedisce che x superi il limite destro
        const maxY = height - 1; // Impedisce che y superi il limite inferiore

        // Limiti per la posizione del cursore
        const x = Math.max(0, Math.min(offsetX, maxX));
        const y = Math.max(0, Math.min(offsetY, maxY));

        setMagnifierPosition({ x, y });
    };

    return (
        <Box>
            <Box
                position="relative"
                width={width}
                height={height}
                overflow="hidden"
                onMouseEnter={() => !disabled && setShowMagnifier(true)}
                onMouseLeave={() => !disabled && setShowMagnifier(false)}
                onClick={() => !disabled && onClick()}
                onMouseMove={handleMouseMove}
                sx={{ cursor: !disabled ? "crosshair" : "default" }}
            >
                {/* Background zoom */}
                <Box
                    sx={{
                        position: "absolute",
                        width: width,
                        height: height,
                        backgroundImage: `url(${src})`,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: `${width * zoom}px ${height * zoom}px`,
                        // Assicurati che lo sfondo venga centrato correttamente
                        backgroundPosition: `-${Math.max(
                            0,
                            Math.min(
                                magnifierPosition.x * zoom - width / 2,
                                width * zoom - width
                            )
                        )}px -${Math.max(
                            0,
                            Math.min(
                                magnifierPosition.y * zoom - height / 2,
                                height * zoom - height
                            )
                        )}px`,
                        display: !disabled && showMagnifier ? "block" : "none",
                        pointerEvents: "none",
                    }}
                />

                {/* Immagine originale */}
                <img
                    src={src}
                    alt="Magnifiable"
                    width={width}
                    height={height}
                    style={{
                        display: !disabled && showMagnifier ? "none" : "block",
                    }}
                />
            </Box>
        </Box>
    );
};

export default MagnifierImage;
